import React from 'react';
import { ArrowLeft } from 'lucide-react';
import useModal from '@/hooks/useModal';
import { Button, Typography } from '@/components/common';
import { Textarea } from '@/components/form';
import { FieldValues, UseFormRegister } from 'react-hook-form';
import Icons from '@/const/icons';

interface Props {
	isOpen: boolean;
	onClose: () => void;
	onBack: () => void;
	onPreview: () => void;
	onSubmit: (emailText: string) => void;
	emailText: string;
	onEmailTextChange: (text: string) => void;
	register: UseFormRegister<FieldValues>;
	isSendFromAdministration?: boolean;
}

export const EmailLanguageModal: React.FC<Props> = ({
	isOpen,
	onClose,
	onBack,
	onPreview,
	onSubmit,
	emailText,
	onEmailTextChange,
	register,
	isSendFromAdministration = false,
}) => {
	const { Modal, show, hide } = useModal();

	React.useEffect(() => {
		if (isOpen) {
			show();
		} else {
			hide();
		}
	}, [isOpen]);

	const hasContent = emailText.trim().length > 0;

	const handleClose = () => {
		hide();
		onClose();
	};

	const handleBack = () => {
		hide();
		onBack();
	};

	return (
		<>
			<Modal
				title="Email Language"
				noMaxHeight
				withXButton
				onClose={handleClose}
				backgroundColor="white"
				classNameTitle="!mb-0 w-full text-left"
				classNameCardContainer="w-full !max-w-[730px] !px-8 !py-8"
				classNameContainerChildren="flex flex-col overflow-visible lg:!px-0"
			>
				<div className="flex flex-col mt-6 text-left">
					<Typography type="semibold-16">Email Content</Typography>

					<div className="flex items-start gap-4 mt-4">
						<div className="flex flex-col flex-1">
							<Typography type="regular-16">Message to Clients</Typography>
							<Typography type="regular-12" className="text-dark-60 mt-1">
								This message will be included in the email sent to the clients
								along with the link to the questions.
							</Typography>
						</div>

						{!isSendFromAdministration && (
							<div className="flex items-center justify-center self-center">
								<Button
									decoration="secondary"
									size="fit"
									disabled={!hasContent}
									className="whitespace-nowrap !h-[30px]"
									icon={Icons.arrowUpRight}
									iconRight={true}
									onClick={onPreview}
									label="View Preview"
									classNameButton="!size-4"
								/>
							</div>
						)}
					</div>

					<div className="mt-1">
						<Textarea
							name="emailContent"
							register={register}
							rows={6}
							customPlaceholder="Hello, we need some additional information to proceed with your case..."
							onChangeCustom={(e) =>
								onEmailTextChange((e.target as HTMLTextAreaElement).value)
							}
							defaultValue={emailText}
						/>
					</div>

					<div className="flex items-center justify-between mt-14">
						<button
							type="button"
							onClick={handleBack}
							className="flex items-center gap-2 outline-none"
						>
							<ArrowLeft className="w-5 h-5 text-opacity-black" />
							<span className="text-base text-opacity-black">
								Back to Create
							</span>
						</button>

						<Button
							decoration="primary"
							size="fit"
							disabled={!hasContent}
							className="!h-12"
							onClick={() => onSubmit(emailText)}
						>
							{isSendFromAdministration ? 'Save Changes' : 'Send Follow-up'}
						</Button>
					</div>
				</div>
			</Modal>
		</>
	);
};
